import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthUser } from "../../contexts/AuthUserContext";
import { useAuth0 } from "../auth/DevAuth0Provider";
import { useTheme } from "../../contexts/ThemeContext";
import { Icon } from "@iconify/react/dist/iconify.js";

const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const ProfileButton = ({ isCollapsed }) => {
    const user = useAuthUser();
    const { logout } = useAuth0();
    const navigate = useNavigate();
    const { theme, toggleTheme, useSystemDefault, setSystemDefaultEnabled } = useTheme();
    const [isExpanded, setIsExpanded] = useState(false);
    const dropdownRef = useRef(null);

    const handleLogout = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        console.log("ProfileButton: Initiating logout...");
        // Use the logout function from context which includes API call
        logout({ logoutReason: 'manual' });
    };

    const handleSettingsClick = () => {
        navigate('/app/settings');
        setIsExpanded(false);
    };

    const handleHelpClick = () => {
        navigate('/app/help');
        setIsExpanded(false);
    };

    const handleLightTheme = () => {
        setSystemDefaultEnabled(false);
        if (theme !== 'light') {
            toggleTheme();
        }
    };

    const handleDarkTheme = () => {
        setSystemDefaultEnabled(false);
        if (theme !== 'dark') {
            toggleTheme();
        }
    };

    const handleSystemDefaultToggle = () => {
        setSystemDefaultEnabled(!useSystemDefault);
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsExpanded(false);
            }
        };

        if (isExpanded) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isExpanded]);

    return (
        <div className={`relative transition-[width] duration-300 overflow-visible ${isCollapsed ? 'w-[52px]' : 'w-[200px]'}`} ref={dropdownRef}>
            {/* Profile Button */}
            {isCollapsed ? (
                <div
                    className="flex items-center justify-center cursor-pointer"
                    onClick={() => setIsExpanded(!isExpanded)}
                >
                    <div className="w-[36px] h-[36px] rounded-md bg-primary-20 flex items-center justify-center flex-shrink-0">
                        <span className="text-primary font-bold text-sm select-none">{getInitials(user.name)}</span>
                    </div>
                </div>
            ) : (
                <div
                    className="flex flex-row items-center justify-between w-full cursor-pointer transition-all duration-200 gap-2"
                    onClick={() => setIsExpanded(!isExpanded)}
                >
                    <div className="user-label flex flex-row items-center flex-1 min-w-0 overflow-hidden gap-2">
                        <img src={user.picture} className="w-[36px] h-[36px] rounded-md flex-shrink-0" alt="User Logo" />
                        <div className="flex flex-col gap-1 min-w-0 overflow-hidden w-full">
                            <span className="font-semibold text-secondary truncate">{user.name}</span>
                            <span className='text-font-secondary font-medium text-xs truncate'>{user.role === 'helios_admin' ? 'Helios Admin' : 'Tenant Admin'}</span>
                        </div>
                    </div>
                    <div className="ml-1 flex-shrink-0 flex flex-row items-center justify-center group w-8 h-8 rounded-md bg-secondary-20 hover:bg-secondary transition-colors duration-300">
                        <button className="w-full h-full flex flex-row items-center justify-center">
                            <Icon
                                icon={'material-symbols:settings-rounded'}
                                className="text-xl text-secondary group-hover:text-background rounded-md transition-transform duration-300"
                            />
                        </button>
                    </div>
                </div>
            )}

            <div className={`absolute bottom-[120%] w-[200px] left-0 right-0 flex flex-col items-center justify-center gap-2 transition-opacity duration-200 ${!isExpanded ? 'pointer-events-none opacity-0 invisible' : 'opacity-100 visible'}`}>
                {/* Theme Toggle Popup */}
                <div className={`w-full rounded-md z-50 transition-all duration-200 ${
                    isExpanded 
                        ? 'opacity-100 translate-y-0 scale-100' 
                        : 'opacity-0 translate-y-2 scale-95 pointer-events-none'
                }`} style={{ bottom: '' }}>
                    <div className="flex flex-row items-center justify-between gap-1 p-2 card-container">
                        <button
                            onClick={handleLightTheme}
                            className={`p-3 rounded-md transition-all duration-300 flex gap-2 items-center justify-center ${
                                theme === 'light' && !useSystemDefault 
                                    ? 'tag-primary' 
                                    : 'bg-transparent hover:bg-bgSecondary'
                            }`}
                            title="Light Theme"
                        >
                            <Icon 
                                icon="mdi:weather-sunny" 
                                className={`text-xl lg:text-2xl ${
                                    theme === 'light' && !useSystemDefault 
                                        ? 'text-primary' 
                                        : 'text-font-secondary'
                                }`}
                            />
                            <span className="block lg:hidden">Light</span>
                        </button>
                        <button
                            onClick={handleDarkTheme}
                            className={`p-3 rounded-md transition-all duration-300 flex gap-2 items-center justify-center ${
                                theme === 'dark' && !useSystemDefault 
                                    ? 'tag-secondary' 
                                    : 'bg-transparent hover:bg-bgSecondary'
                            }`}
                            title="Dark Theme"
                        >
                            <Icon 
                                icon="mdi:weather-night" 
                                className={`text-xl lg:text-2xl ${
                                    theme === 'dark' && !useSystemDefault 
                                        ? 'text-primary' 
                                        : 'text-font-secondary'
                                }`} 
                            />
                            <span className="block lg:hidden">Dark</span>
                        </button>
                        <button
                            onClick={handleSystemDefaultToggle}
                            className={`p-3 rounded-md transition-all duration-300 flex gap-2 items-center justify-center ${
                                useSystemDefault 
                                    ? 'tag-secondary' 
                                    : 'bg-transparent hover:bg-bgSecondary'
                            }`}
                            title="System Default"
                        >
                            <Icon 
                                icon="mdi:monitor" 
                                className={`text-xl lg:text-2xl ${
                                    useSystemDefault 
                                        ? 'text-secondary' 
                                        : 'text-font-secondary'
                                }`} 
                            />
                            <span className={`block lg:hidden`}>Default</span>
                        </button>
                    </div>
                </div>

                {/* Dropdown Menu */}
                <div className={`button-container w-full rounded-md border border-bgAccent z-40 transition-all duration-200 ${
                    isExpanded 
                        ? 'opacity-100 translate-y-0 scale-100' 
                        : 'opacity-0 translate-y-2 scale-95 pointer-events-none'
                }`}>
                    <div className="flex flex-col">
                        <button
                            onClick={handleSettingsClick}
                            className="flex flex-row items-center gap-1.5 px-2 py-3 hover:bg-bgSecondary transition-colors duration-200 first:rounded-t-md"
                        >
                            <Icon icon="material-symbols:settings-outline-rounded" className="text-xl text-primary" />
                            <span className="text-font-primary">Settings</span>
                        </button>
                        <hr className="border-bgAccent" />
                        <button
                            onClick={handleHelpClick}
                            className="flex flex-row items-center gap-1.5 px-2 py-3 hover:bg-bgSecondary transition-colors duration-200"
                        >
                            <Icon icon="material-symbols:help-outline-rounded" className="text-xl text-primary" />
                            <span className="text-font-primary">Help</span>
                        </button>
                        <hr className="border-bgAccent" />
                        <button
                            onClick={handleLogout}
                            className="flex flex-row items-center gap-1.5 px-2 py-3 hover:bg-bgSecondary transition-colors duration-200 last:rounded-b-md text-error"
                        >
                            <Icon icon="mdi:logout" className="text-xl text-error" />
                            <span className="text-error">Sign Out</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProfileButton;
